import React, { useState, useEffect, useRef } from 'react'; // Refreshed
import { X, ChevronRight, Copy, Check, UserPlus } from 'lucide-react';
import { marketingService, userService, type ReferrerOption } from '../../../services/api';
import Snackbar from '../../common/Snackbar';
import Select from '../../common/Select';
import ReactCountryFlag from 'react-country-flag';
import { useCountryCodeSelection } from '../../../hooks/useCountryCodeSelection';
import CountrySearch from '../../common/CountrySearch';
import { validateMobile, getExpectedLength, getDigitRuleDescription, getIsoFromEmoji } from '../../../utils/countryCodes';
import { useAppSelector } from '../../../store/hooks';
import type { RootState } from '../../../store';

export interface CreateUserProps {
    isOpen: boolean;
    onClose: () => void;
    onSuccess: (mobile?: string) => void;
    adminReferralCode?: string; // Optional context
    initialRole?: string;
}

export interface CreateUserFormData {
    mobile: string;
    first_name: string;
    last_name: string;
    email: string;
    role: string;
    referral_code: string;
    is_test: boolean;
    refered_by_mobile?: string;
    refered_by_name?: string;
    isabletorefer: boolean;
}


export const CreateUser = ({ isOpen, onClose, onSuccess, adminReferralCode, initialRole }: CreateUserProps) => {
    const [formData, setFormData] = useState<CreateUserFormData>({
        mobile: '',
        first_name: '',
        last_name: '',
        email: '',
        role: 'Investor',
        referral_code: '',
        is_test: false,
        refered_by_mobile: '',
        refered_by_name: '',
        isabletorefer: false,
    });
    const [loading, setLoading] = useState(false);
    const [, setErr] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [snackbar, setSnackbar] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

    // Employee, Admin, and SuperAdmin callers get the "search by mobile" referral-code
    // lookup below — everyone else keeps the original behavior of the Referral Code field
    // just showing their own code.
    const { adminRole } = useAppSelector((state: RootState) => state.auth);
    const effectiveCallerRole = (adminRole || '').toLowerCase();
    const canLookupReferrer = effectiveCallerRole.includes('employee') || effectiveCallerRole.includes('admin');

    // Referral-code-by-mobile lookup (Employee/Admin/SuperAdmin) — resolves a mobile number to that
    // user's referral code via the same GET /users/customers/{mobile} details endpoint used
    // elsewhere in this dashboard, then fills the (still read-only) Referral Code field above
    // with the result, same idea as crm_app's onboarding referrer field but scoped to mobile.
    const [referrerMobileQuery, setReferrerMobileQuery] = useState('');
    const [resolvedReferrer, setResolvedReferrer] = useState<{
        mobile: string; referral_code: string; name: string; role: string;
    } | null>(null);
    const [resolvingReferrer, setResolvingReferrer] = useState(false);
    const [referrerLookupError, setReferrerLookupError] = useState<string | null>(null);
    const [copiedReferralCode, setCopiedReferralCode] = useState(false);
    // Direct: referred by the SuperAdmin (their code fills in). Indirect: search the referrer by mobile.
    const [referralType, setReferralType] = useState<'direct' | 'indirect'>('direct');
    // Bumped on every Direct load and on switching type, so a slow answer can't land after
    // the staff member has moved on to Indirect.
    const directRequest = useRef(0);
    // Matches for the digits typed so far; the list shows while the box is focused.
    const [referrerOptions, setReferrerOptions] = useState<ReferrerOption[]>([]);
    const [showReferrerList, setShowReferrerList] = useState(false);

    const {
        countryCodes,
        selectedCountryCode,
        setSelectedCountryCode,
        isDropdownOpen,
        setIsDropdownOpen,
        dropdownRef,
        selectedFlagCode,
        processMobileOnCountryChange,
        countrySearch,
        setCountrySearch,
        shownCountryCodes,
    } = useCountryCodeSelection(isOpen);

    const handleSelectCountry = (newCode: string, flag?: string) => {
        setSelectedCountryCode(newCode, flag);
        
        setFormData(prev => {
            const { slicedMobile, errorMessage } = processMobileOnCountryChange(newCode, prev.mobile, { fullLengthMsg: true });
            
            setValidationErrors(prevErrors => {
                const newErrors = { ...prevErrors };
                if (errorMessage) {
                    newErrors.mobile = errorMessage;
                } else {
                    delete newErrors.mobile;
                }
                return newErrors;
            });

            return {
                ...prev,
                mobile: slicedMobile
            };
        });
    };

    // Reset form when opened
    useEffect(() => {
        if (isOpen) {
            setFormData({
                mobile: '',
                first_name: '',
                last_name: '',
                email: '',
                role: initialRole || 'Investor',
                // Staff callers start on Direct (filled in below from the API); others see their own code.
                referral_code: canLookupReferrer ? '' : (adminReferralCode || ''),
                is_test: false,
                refered_by_mobile: '',
                refered_by_name: '',
                isabletorefer: false,
            });
            setErr(null);
            setValidationErrors({});
            setReferrerMobileQuery('');
            setResolvedReferrer(null);
            setReferrerLookupError(null);
            setReferralType('direct');
            setReferrerOptions([]);
            setShowReferrerList(false);
            if (canLookupReferrer) loadDirectReferrer();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, adminReferralCode, initialRole, canLookupReferrer]);

    // Search as you type (Employee/Admin/SuperAdmin callers, Indirect): from 2 digits, users whose
    // mobile contains them (GET /users/customers?search=) — investors only, so a full 10-digit
    // number is also looked up directly (GET /users/customers/{mobile}), which finds staff too.
    // Only people with a referral code can refer. Picking one fills the Referral Code field.
    useEffect(() => {
        if (!isOpen || !canLookupReferrer || referralType !== 'indirect') return;
        const query = referrerMobileQuery.trim();
        if (query.length < 2 || (resolvedReferrer && resolvedReferrer.mobile === query)) {
            setReferrerOptions([]);
            setReferrerLookupError(null);
            setResolvingReferrer(false);
            return;
        }
        let cancelled = false;
        setResolvingReferrer(true);
        setReferrerLookupError(null);
        const t = setTimeout(async () => {
            // The users list sends the code as "referralcode"; the details endpoint as "referral_code".
            const codeOf = (u: any): string => u?.referral_code || u?.referralcode || '';
            const toOption = (u: any): ReferrerOption => ({
                mobile: u.mobile, referral_code: codeOf(u), role: u.role || '',
                name: `${u.first_name || ''} ${u.last_name || ''}`.trim(),
            });
            const [list, exact] = await Promise.all([
                userService.getUsers({ search: query, limit: 10, page: 1 }).then(r => r?.users || []).catch(() => []),
                query.length === 10 ? userService.getUserDetails(query).then(r => (r?.user ? [r.user] : [])).catch(() => []) : Promise.resolve([]),
            ]);
            if (cancelled) return;
            const seen = new Set<string>();
            const options = [...exact, ...list]
                .filter((u: any) => u?.mobile && String(u.mobile).includes(query) && codeOf(u))
                .filter((u: any) => !seen.has(u.mobile) && !!seen.add(u.mobile))
                .map(toOption);
            setReferrerOptions(options);
            setReferrerLookupError(options.length ? null : 'No one with a referral code matches this number');
            setResolvingReferrer(false);
        }, 300);
        return () => { cancelled = true; clearTimeout(t); };
    }, [referrerMobileQuery, isOpen, canLookupReferrer, referralType, resolvedReferrer]);

    const pickReferrer = (r: ReferrerOption) => {
        setResolvedReferrer({ mobile: r.mobile, referral_code: r.referral_code, name: r.name || '', role: r.role || '' });
        setReferrerMobileQuery(r.mobile);
        setReferrerOptions([]);
        setShowReferrerList(false);
        setReferrerLookupError(null);
        setFormData(prev => ({ ...prev, referral_code: r.referral_code }));
    };

    // Direct: the referrer an Admin set in the CRM (GET /marketing/onboard-config →
    // resolve-referrer), exactly as the CRM's onboarding form gets it — not a fixed code.
    const loadDirectReferrer = async () => {
        const req = ++directRequest.current;
        setResolvingReferrer(true);
        setReferrerLookupError(null);
        try {
            const r = await marketingService.getDirectReferrer();
            if (req !== directRequest.current) return;
            if (!r.referral_code) throw new Error('The direct referrer has no referral code');
            setResolvedReferrer({ ...r, role: 'Direct' });
            setFormData(prev => ({ ...prev, referral_code: r.referral_code }));
        } catch (e: any) {
            if (req !== directRequest.current) return;
            setResolvedReferrer(null);
            setFormData(prev => ({ ...prev, referral_code: '' }));
            const msg = e?.response?.data?.detail || e?.message;
            setReferrerLookupError(`Couldn’t load the direct referrer${msg ? ` (${msg})` : ''}. Choose Indirect and search the referrer.`);
        } finally {
            if (req === directRequest.current) setResolvingReferrer(false);
        }
    };

    const chooseReferralType = (type: 'direct' | 'indirect') => {
        directRequest.current++;
        setResolvingReferrer(false);
        setReferralType(type);
        setReferrerMobileQuery('');
        setReferrerOptions([]);
        setResolvedReferrer(null);
        setReferrerLookupError(null);
        if (type !== 'direct') { setFormData(prev => ({ ...prev, referral_code: '' })); return; }
        loadDirectReferrer();
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;

        // Mobile Validation: Only allow digits
        if (name === 'mobile' && !/^\d*$/.test(value)) {
            return;
        }

        // Name Validation: Only allow alphabets and spaces
        if ((name === 'first_name' || name === 'last_name') && !/^[a-zA-Z\s]*$/.test(value)) {
            return;
        }

        // Email Validation: Only allow alphanumeric, @, ., _, and -
        if (name === 'email' && !/^[a-zA-Z0-9@._-]*$/.test(value)) {
            return;
        }

        if (type === 'checkbox') {
            const checked = (e.target as HTMLInputElement).checked;
            setFormData(prev => ({ ...prev, [name]: checked }));
        } else {
            setFormData(prev => ({ ...prev, [name]: value }));
        }

        // Check mobile validation immediately if full length is reached or clear inline error
        if (name === 'mobile') {
            const expectedLen = getExpectedLength(selectedCountryCode);
            if (value.length === expectedLen) {
                const isValid = validateMobile(selectedCountryCode, value);
                if (!isValid) {
                    setValidationErrors(prev => ({
                        ...prev,
                        mobile: `Invalid phone number format for ${selectedCountryCode}`
                    }));
                } else if (validationErrors.mobile) {
                    setValidationErrors(prev => {
                        const newErrs = { ...prev };
                        delete newErrs.mobile;
                        return newErrs;
                    });
                }
            } else if (validationErrors.mobile) {
                setValidationErrors(prev => {
                    const newErrs = { ...prev };
                    delete newErrs.mobile;
                    return newErrs;
                });
            }
        } else if (validationErrors[name]) {
            setValidationErrors(prev => {
                const newErrs = { ...prev };
                delete newErrs[name];
                return newErrs;
            });
        }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        if (name === 'mobile' && value.length > 0) {
            const expectedLen = getExpectedLength(selectedCountryCode);
            const isValid = validateMobile(selectedCountryCode, value);
            if (!isValid) {
                const errorMsg = value.length !== expectedLen
                    ? `Mobile number must be ${getDigitRuleDescription(selectedCountryCode)}`
                    : `Invalid phone number format for ${selectedCountryCode}`;
                setValidationErrors(prev => ({ ...prev, mobile: errorMsg }));
            }
        } else if (name === 'first_name' && value.trim().length === 0) {
            setValidationErrors(prev => ({ ...prev, first_name: 'First name is required' }));
        } else if (name === 'email' && value.trim().length > 0 && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
            setValidationErrors(prev => ({ ...prev, email: 'Please enter a valid email address' }));
        }
    };

    const validate = () => {
        const errors: Record<string, string> = {};

        // Mobile Validation
        if (!formData.mobile) {
            errors.mobile = 'Mobile number is required';
        } else {
            const isValid = validateMobile(selectedCountryCode, formData.mobile);
            if (!isValid) {
                const expectedLen = getExpectedLength(selectedCountryCode);
                if (formData.mobile.length !== expectedLen) {
                    errors.mobile = `Mobile number must be ${getDigitRuleDescription(selectedCountryCode)}`;
                } else {
                    errors.mobile = 'Invalid phone number format';
                }
            }
        }

        // Name Validation
        if (!formData.first_name || formData.first_name.trim().length < 1) {
            errors.first_name = 'First name is required';
        }

        // Email Validation (Optional)
        const trimmedEmail = formData.email.trim();
        if (trimmedEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
            errors.email = 'Please enter a valid email address';
        }

        // Referral Code Validation (Required for new users)
        if (!formData.referral_code) {
            errors.referral_code = 'Referral code is required';
        }

        return errors;
    };

    // Check if form is valid for enabling button
    const isFormValid = (() => {
        if (!formData.mobile || !validateMobile(selectedCountryCode, formData.mobile)) return false;
        if (!formData.first_name || formData.first_name.trim().length < 1) return false;
        if (!formData.referral_code) return false;
        return true;
    })();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const errors = validate();
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors);
            return;
        }

        setLoading(true);
        setErr(null);

        try {
            // Adapt formData to API expectation based on user request
            const isabletoreferr = formData.role === 'Employee' || formData.role === 'SpecialCategory';
            const formattedMobile = formData.mobile;

            const apiData = {
                mobile: formattedMobile,
                country_code: selectedCountryCode.replace('+', ''),
                first_name: formData.first_name,
                last_name: formData.last_name,
                email: formData.email.trim(),
                role: formData.role,
                referred_by_code: formData.referral_code,
                isabletorefer: isabletoreferr,
                isTestAccount: !!formData.is_test,
            };

            const result = await userService.createUser(apiData);

            if (result.error) {
                setErr(result.error);
                setSnackbar({ message: result.error, type: 'error' });
            } else {
                onSuccess(formattedMobile);
                onClose();
            }
        } catch (error: any) {
            const errorMessage = error.message || 'Failed to create user';
            setErr(errorMessage);
            setSnackbar({ message: errorMessage, type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    const roleName = formData.role === 'SpecialCategory' ? 'Special Category' : formData.role;
    const title = `Add New ${roleName}`;
    const submitText = `Create ${roleName}`;

    return (
        <>
            <div className="fixed inset-0 z-[9999] flex items-center justify-center overflow-y-auto p-4 bg-black/40 backdrop-blur-sm animate-fadeIn">
                <div className="bg-white rounded-2xl shadow-xl w-full max-w-xl overflow-hidden transform transition-all animate-scaleIn border border-white/20 max-h-[92vh] flex flex-col">
                    {/* Header — kept as its own separate bar */}
                    <div className="bg-gradient-to-r from-[#527d60] to-[#43674f] p-4 flex items-center justify-between text-white shrink-0">
                        <div className="flex items-center gap-2.5">
                            <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center backdrop-blur-md">
                                <UserPlus size={18} className="text-white" />
                            </div>
                            <div>
                                <h3 className="text-base font-bold">{title}</h3>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            className="p-1.5 hover:bg-white/10 rounded-lg transition-all"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    {/* Form Content — single flowing section, flat white background */}
                    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
                      <div className="flex-1 min-h-0 overflow-y-auto bg-white p-5 space-y-4">
                            {/* Role & Mobile Row */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-gray-700">Role</label>
                                    <Select
                                        value={formData.role}
                                        onChange={(role) => setFormData(prev => ({ ...prev, role }))}
                                        options={[
                                            { value: 'Investor', label: 'Investor' },
                                            ...((effectiveCallerRole.includes('admin') || effectiveCallerRole.includes('superadmin'))
                                                ? [{ value: 'SpecialCategory', label: 'Special Category' }]
                                                : []),
                                        ]}
                                        className="h-11 w-full px-3.5 rounded-lg border border-gray-300 focus:border-[#1B4332] bg-white text-sm text-gray-800"
                                    />
                                </div>

                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-gray-700">Mobile Number</label>
                                    <div className="relative" ref={dropdownRef}>
                                        <div className={`h-11 flex items-center rounded-lg border bg-white transition-all focus-within:ring-2 focus-within:ring-emerald-500/20 ${validationErrors.mobile ? 'border-red-300 focus-within:border-red-400' : 'border-gray-300 focus-within:border-[#1B4332]'}`}>
                                            <button
                                                type="button"
                                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                                className="flex items-center gap-1 h-full shrink-0 pl-3.5 pr-2 border-r border-gray-200 text-gray-700 text-xs font-semibold outline-none cursor-pointer hover:text-gray-900"
                                            >
                                                {selectedFlagCode && (
                                                    <div className="flex items-center shrink-0 border border-gray-200 rounded overflow-hidden w-4 h-3">
                                                        <ReactCountryFlag
                                                            countryCode={getIsoFromEmoji(selectedFlagCode)}
                                                            svg
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                objectFit: 'cover'
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                                <span className="text-[11px]">{selectedCountryCode}</span>
                                                <ChevronRight size={10} className={`text-gray-400 transition-transform shrink-0 ${isDropdownOpen ? 'rotate-90' : ''}`} />
                                            </button>
                                            <input
                                                type="tel"
                                                name="mobile"
                                                value={formData.mobile}
                                                onChange={handleChange}
                                                onBlur={handleBlur}
                                                placeholder="Enter mobile number"
                                                maxLength={getExpectedLength(selectedCountryCode)}
                                                className="flex-1 min-w-0 px-3.5 py-2.5 rounded-r-lg outline-none placeholder:text-gray-400 text-sm text-gray-800 bg-transparent"
                                            />
                                        </div>

                                        {isDropdownOpen && (
                                            <div className="absolute left-0 top-full mt-1.5 w-64 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-xl p-1.5 pt-0 z-50 shadow-lg scrollbar-thin scrollbar-thumb-gray-200 animate-in fade-in slide-in-from-top-1 duration-150">
                                                <CountrySearch value={countrySearch} onChange={setCountrySearch}
                                                    onClose={() => setIsDropdownOpen(false)}
                                                    onPickFirst={() => {
                                                        const first = shownCountryCodes[0];
                                                        if (first) { handleSelectCountry(first.code, first.flag); setIsDropdownOpen(false); }
                                                    }} />
                                                {countryCodes.length === 0 ? (
                                                    <div className="text-gray-400 text-[11px] py-1.5 px-2">Loading countries...</div>
                                                ) : shownCountryCodes.length === 0 ? (
                                                    <div className="text-gray-400 text-[11px] py-1.5 px-2">No country matches "{countrySearch.trim()}"</div>
                                                ) : (
                                                    shownCountryCodes.map((c, idx) => (
                                                        <button
                                                            key={`${c.code}-${c.flag || idx}`}
                                                            type="button"
                                                            onClick={() => {
                                                                handleSelectCountry(c.code, c.flag);
                                                                setIsDropdownOpen(false);
                                                            }}
                                                            className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-gray-50 text-gray-700 text-left transition-colors text-xs font-medium"
                                                        >
                                                            {c.flag && (
                                                                <div className="flex items-center shrink-0 border border-gray-150 rounded overflow-hidden w-5 h-3">
                                                                    <ReactCountryFlag
                                                                        countryCode={getIsoFromEmoji(c.flag)}
                                                                        svg
                                                                        style={{
                                                                            width: '100%',
                                                                            height: '100%',
                                                                            objectFit: 'cover'
                                                                        }}
                                                                    />
                                                                </div>
                                                            )}
                                                            <span className="truncate flex-1 text-gray-700">{c.country || 'Unknown'}</span>
                                                            <span className="text-gray-400 font-semibold text-[10px]">{c.code}</span>
                                                        </button>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    {validationErrors.mobile && <p className="mt-1 text-xs text-red-600 font-medium">{validationErrors.mobile}</p>}
                                </div>
                            </div>

                            {/* Name Fields */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-gray-700">First Name</label>
                                    <input
                                        type="text"
                                        name="first_name"
                                        value={formData.first_name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="First Name"
 className={`h-11 w-full px-3.5 py-2.5 rounded-lg border bg-white outline-none transition-all placeholder:text-gray-400 text-sm text-gray-800 ${validationErrors.first_name ?'border-red-300 focus:border-red-400':'border-gray-300 focus:border-[#1B4332]'}`}
                                    />
                                    {validationErrors.first_name && <p className="mt-1 text-xs text-red-600 font-medium">{validationErrors.first_name}</p>}
                                </div>
                                <div>
                                    <label className="mb-1.5 block text-sm font-medium text-gray-700">Last Name (Optional)</label>
                                    <input
                                        type="text"
                                        name="last_name"
                                        value={formData.last_name}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        placeholder="Last Name"
 className={`h-11 w-full px-3.5 py-2.5 rounded-lg border bg-white outline-none transition-all placeholder:text-gray-400 text-sm text-gray-800 ${validationErrors.last_name ?'border-red-300 focus:border-red-400':'border-gray-300 focus:border-[#1B4332]'}`}
                                    />
                                    {validationErrors.last_name && <p className="mt-1 text-xs text-red-600 font-medium">{validationErrors.last_name}</p>}
                                </div>
                            </div>

                            {/* Email */}
                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-gray-700">Email (Optional)</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    placeholder="Enter email address"
 className={`h-11 w-full px-3.5 py-2.5 rounded-lg border bg-white focus:border-[#1B4332] outline-none transition-all placeholder:text-gray-400 text-sm text-gray-800 ${validationErrors.email ?'border-red-300 focus:border-red-400':'border-gray-300'}`}
                                />
                                {validationErrors.email && <p className="mt-1 text-xs text-red-600 font-medium">{validationErrors.email}</p>}
                            </div>

                            {canLookupReferrer && (
                                <div>
                                    <span className="mb-1.5 block text-sm font-medium text-gray-700">Referred by</span>
                                    <div role="radiogroup" aria-label="Referred by" className="grid grid-cols-2 gap-2">
                                        {([['direct', 'Direct'], ['indirect', 'Indirect']] as const).map(([v, label]) => (
                                            <button key={v} type="button" role="radio" aria-checked={referralType === v} onClick={() => chooseReferralType(v)}
                                                className={`h-11 rounded-lg border text-sm font-semibold transition-colors ${referralType === v ? 'border-[#1B4332] bg-[#1B4332] text-white' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'}`}>
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {canLookupReferrer && referralType === 'indirect' && (
                                <div className="relative">
                                    <label htmlFor="referrer-mobile" className="mb-1.5 block text-sm font-medium text-gray-700">Search Referral Code by Mobile</label>
                                    <input
                                        id="referrer-mobile"
                                        type="tel"
                                        inputMode="numeric"
                                        autoComplete="off"
                                        role="combobox"
                                        aria-expanded={showReferrerList && referrerOptions.length > 0}
                                        aria-controls="referrer-options"
                                        value={referrerMobileQuery}
                                        onChange={e => {
                                            const v = e.target.value.replace(/\D/g, '').slice(0, 10);
                                            setReferrerMobileQuery(v);
                                            setShowReferrerList(true);
                                            if (resolvedReferrer && v !== resolvedReferrer.mobile) {
                                                setResolvedReferrer(null);
                                                setFormData(prev => ({ ...prev, referral_code: '' }));
                                            }
                                        }}
                                        onFocus={() => setShowReferrerList(true)}
                                        onBlur={() => setTimeout(() => setShowReferrerList(false), 150)}
                                        placeholder="Type 2 or more digits"
                                        className="h-11 w-full px-3.5 py-2.5 rounded-lg border border-gray-300 bg-white focus:border-[#1B4332] outline-none transition-all text-sm text-gray-800"
                                    />
                                    {showReferrerList && referrerOptions.length > 0 && (
                                        <ul id="referrer-options" role="listbox" className="absolute z-20 left-0 right-0 mt-1 max-h-64 overflow-y-auto rounded-lg border border-gray-200 bg-white shadow-lg">
                                            {referrerOptions.map(r => (
                                                <li key={r.mobile} role="option" aria-selected={false}>
                                                    <button type="button" onMouseDown={e => e.preventDefault()} onClick={() => pickReferrer(r)}
                                                        className="w-full flex items-center gap-3 px-3.5 py-2.5 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0">
                                                        <span className="min-w-0 flex-1">
                                                            <span className="block text-sm font-semibold text-gray-900 truncate">{r.name || 'No name'}</span>
                                                            <span className="block text-xs text-gray-500 font-mono">{r.mobile}{r.role ? <span className="font-sans"> · {r.role}</span> : null}</span>
                                                        </span>
                                                        <span className="shrink-0 font-mono text-xs font-semibold text-gray-700">{r.referral_code}</span>
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                    {resolvingReferrer && <p className="mt-1 text-xs text-gray-400">Searching…</p>}
                                    {!resolvingReferrer && !resolvedReferrer && referrerLookupError && referrerMobileQuery.length >= 2 && (
                                        <p className="mt-1 text-xs text-red-600 font-medium">{referrerLookupError}</p>
                                    )}
                                </div>
                            )}

                            {canLookupReferrer && referralType === 'direct' && resolvingReferrer && <p className="-mt-2 text-xs text-gray-400">Loading direct referrer…</p>}
                            {canLookupReferrer && referralType === 'direct' && !resolvingReferrer && referrerLookupError && <p className="-mt-2 text-xs text-red-600 font-medium">{referrerLookupError}</p>}

                            {canLookupReferrer && resolvedReferrer && (
                                <div className="flex items-center gap-2 text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-100 rounded-lg px-3 py-2">
                                    <span className="font-semibold">{resolvedReferrer.name || resolvedReferrer.mobile}</span>
                                    {resolvedReferrer.role && <span className="text-emerald-600">({resolvedReferrer.role})</span>}
                                    <span className="ml-auto font-mono font-semibold text-emerald-600">{resolvedReferrer.referral_code}</span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            navigator.clipboard.writeText(resolvedReferrer.referral_code);
                                            setCopiedReferralCode(true);
                                            setTimeout(() => setCopiedReferralCode(false), 1500);
                                        }}
                                        title="Copy referral code"
                                        className="text-emerald-500 hover:text-emerald-700 bg-transparent border-none cursor-pointer p-0.5 shrink-0"
                                    >
                                        {copiedReferralCode ? <Check size={13} /> : <Copy size={13} />}
                                    </button>
                                </div>
                            )}

                            <div>
                                <label className="mb-1.5 block text-sm font-medium text-gray-700">Referral Code</label>
                                <input
                                    type="text"
                                    name="referral_code"
                                    value={formData.referral_code}
                                    onChange={canLookupReferrer && referralType === 'indirect' ? handleChange : undefined}
                                    readOnly={!canLookupReferrer || referralType === 'direct'}
                                    disabled={!canLookupReferrer}
                                    placeholder={canLookupReferrer ? 'Enter or search a referral code' : undefined}
 className={`h-11 w-full px-3.5 py-2.5 rounded-lg border focus:border-[#1B4332] outline-none transition-all text-sm ${canLookupReferrer ?'text-gray-800 bg-white':'text-gray-500 bg-gray-100 cursor-not-allowed'} ${validationErrors.referral_code ?'border-red-300 focus:border-red-400':'border-gray-300'}`}
                                />
                                {validationErrors.referral_code && <p className="mt-1 text-xs text-red-600 font-medium">{validationErrors.referral_code}</p>}
                            </div>
                      </div>

                      <div className="shrink-0 flex gap-3 px-5 py-4 border-t border-gray-100">
                          <button
                              type="button"
                              onClick={onClose}
                              className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg text-gray-700 font-semibold text-sm hover:bg-gray-50 transition-all active:scale-95"
                          >
                              Cancel
                          </button>
                          <button
                              type="submit"
                              disabled={loading || !isFormValid}
                              className="flex-[1.5] px-4 py-2.5 bg-[#527d60] hover:bg-[#43674f] text-white rounded-lg font-semibold text-sm shadow-sm transition-all active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed cursor-pointer"
                          >
                              {loading ? 'Processing...' : submitText}
                          </button>
                      </div>
                    </form>
                </div>
            </div>

            {/* Snackbar for error messages - rendered outside modal for proper z-index */}
            {
                snackbar && (
                    <Snackbar
                        message={snackbar.message}
                        type={snackbar.type}
                        onClose={() => setSnackbar(null)}
                    />
                )
            }
        </>
    );
};
